import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { buildCategoryCollection, buildSubcategoryId, getCategoryMeta } from '../../utils/categoryCatalog';
import { readPersistedStores } from '../../utils/storeSeed';
import { usePageSeo } from '../../utils/seo';

interface StorePromo {
  id: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isActive: boolean;
  isTopDeal?: boolean;
  image?: string;
}

interface Store {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: StorePromo[];
}

interface CategoryData {
  id: string;
  name: string;
  description: string;
  icon: string;
  bgGradient: string;
  storesCount: number;
  promosCount: number;
  stores: Store[];
  subcategories: string[];
}

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryData[]>([]);
  usePageSeo({
    title: 'Категории промокодов и акций | Promokod.online',
    description: 'Каталог категорий Promokod.online: одежда, электроника, путешествия, красота, образование и другие направления с актуальными промокодами и акциями.',
    canonicalPath: '/categories',
  });

  useEffect(() => {
    const stores = readPersistedStores(localStorage) as Store[];
    const categoriesArray: CategoryData[] = buildCategoryCollection(stores).map((category) => {
      const meta = getCategoryMeta(category.name);

      return {
        id: category.id,
        name: category.name,
        description: meta.description,
        icon: meta.icon,
        bgGradient: meta.bgGradient,
        storesCount: category.stores.length,
        promosCount: category.promosCount,
        stores: category.stores,
        subcategories: category.subcategories,
      };
    });

    setCategories(categoriesArray);
  }, []);

  const handleViewPromocodes = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  const handleViewSubcategory = (categoryId: string, subcategoryName: string) => {
    const subcategoryId = buildSubcategoryId(subcategoryName);
    navigate(`/category/${categoryId}?subcategory=${subcategoryId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-amber-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Все категории товаров
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Найдите промокоды для любой категории товаров. От одежды до электроники - у нас есть скидки на всё!
          </p>
          <div className="flex items-center justify-center gap-8 text-sm opacity-80">
            <div className="flex items-center gap-2">
              <i className="ri-store-line text-lg"></i>
              <span>{categories.reduce((sum, cat) => sum + cat.storesCount, 0)}+ магазинов</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-price-tag-line text-lg"></i>
              <span>{categories.reduce((sum, cat) => sum + cat.promosCount, 0)}+ промокодов</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-refresh-line text-lg"></i>
              <span>Ежедневные обновления</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          {categories.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-apps-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">Категории пока не добавлены</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categories.map(category => (
                <div key={category.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group flex flex-col h-full">
                  <div className="relative h-48 overflow-hidden">
                    <div className={`w-full h-full bg-gradient-to-br ${category.bgGradient} flex items-center justify-center`}>
                      <i className={`${category.icon} text-6xl text-white/90`}></i>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{category.name}</h3>
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-gray-600 mb-4 line-clamp-2 flex-1">{category.description}</p>
                    
                    <div className="flex items-center justify-between mb-4 text-sm">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500">
                          <i className="ri-store-line mr-1"></i>
                          {category.storesCount} магазинов
                        </span>
                        <span className="text-orange-600 font-medium">
                          <i className="ri-price-tag-line mr-1"></i>
                          {category.promosCount} промокодов
                        </span>
                      </div>
                    </div>

                    {/* Подкатегории */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2 font-medium">Подкатегории:</p>
                        <div className="flex flex-wrap gap-1">
                          {category.subcategories.slice(0, 4).map(sub => (
                            <button
                              key={sub}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSubcategory(category.id, sub);
                              }}
                              className="bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-700 text-xs px-2 py-1 rounded-full transition-colors cursor-pointer"
                            >
                              {sub}
                            </button>
                          ))}
                          {category.subcategories.length > 4 && (
                            <span className="text-xs text-gray-400 px-2 py-1">
                              +{category.subcategories.length - 4} ещё
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="mb-6 flex-1">
                      <p className="text-sm text-gray-500 mb-2">Популярные магазины:</p>
                      <div className="flex flex-wrap gap-1">
                        {category.stores.slice(0, 3).map(store => (
                          <span key={store.id} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                            {store.name}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => handleViewPromocodes(category.id)}
                      className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 transition-colors font-medium whitespace-nowrap group-hover:bg-orange-600 mt-auto cursor-pointer"
                    >
                      Смотреть промокоды
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-50 to-amber-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Не нашли нужную категорию?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Свяжитесь с нами, и мы добавим промокоды для интересующих вас магазинов
          </p>
          <button 
            onClick={() => navigate('/contacts')}
            className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            Связаться с нами
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
